import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import {
  EnterprisePosition,
  DocumentMatrixAcl,
  PhysicalArchiveRecord,
  PositionHandoverAuditLog,
  MatrixPermissionEvaluation,
  DocumentGovernanceData
} from '../types/governance';

export interface DocumentGovernanceInspectorProps {
  isOpen: boolean;
  onClose: () => void;
  document: {
    id: string;
    filename: string;
    status?: string;
    metadata?: any;
  } | null;
  onGovernanceUpdated?: () => void;
}

export function DocumentGovernanceInspector({
  isOpen,
  onClose,
  document,
  onGovernanceUpdated
}: DocumentGovernanceInspectorProps) {
  const { tenant } = useAuth();

  // Active Tab: 'matrix' | 'physical'
  const [activeTab, setActiveTab] = useState<'matrix' | 'physical'>('matrix');

  // Loading & Error States
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Governance Data
  const [positions, setPositions] = useState<EnterprisePosition[]>([]);
  const [governanceData, setGovernanceData] = useState<DocumentGovernanceData | null>(null);
  const [permissionEval, setPermissionEval] = useState<MatrixPermissionEvaluation | null>(null);

  // Sub-modal states
  const [showHandoverModal, setShowHandoverModal] = useState<boolean>(false);
  const [showBorrowModal, setShowBorrowModal] = useState<boolean>(false);
  const [showReturnModal, setShowReturnModal] = useState<boolean>(false);
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [showMatrixEditModal, setShowMatrixEditModal] = useState<boolean>(false);

  // Handover form state
  const [handoverTargetPositionId, setHandoverTargetPositionId] = useState<string>('POS_CFO_01');
  const [newOccupantUserId, setNewOccupantUserId] = useState<string>('');
  const [newOccupantName, setNewOccupantName] = useState<string>('');
  const [handoverNote, setHandoverNote] = useState<string>('Bổ nhiệm theo Quyết định số 89/QĐ-WX-HĐQT');
  const [retainReadAccess, setRetainReadAccess] = useState<boolean>(false);

  // Physical Location form state
  const [locWarehouse, setLocWarehouse] = useState<string>('Kho Lưu Trữ Trung Tâm - K02');
  const [locAisle, setLocAisle] = useState<string>('Dãy B');
  const [locShelf, setLocShelf] = useState<string>('Kệ 04');
  const [locBox, setLocBox] = useState<string>('Hộp 12');
  const [locFolder, setLocFolder] = useState<string>('Tập hợp đồng tín dụng Q4/2026');
  const [locArchiveCode, setLocArchiveCode] = useState<string>('ARCHIVE-2026-HN-K02');

  // Physical Borrow form state
  const [borrowerPosId, setBorrowerPosId] = useState<string>('POS_AUDIT_HEAD');
  const [borrowerName, setBorrowerName] = useState<string>(tenant?.name || 'Nguyễn Văn A');
  const [borrowPurpose, setBorrowPurpose] = useState<string>('Phục vụ thanh tra, kiểm toán độc lập Q4');
  const [borrowDueDateDays, setBorrowDueDateDays] = useState<number>(7);

  // Physical Return form state
  const [returnNote, setReturnNote] = useState<string>('Đã kiểm tra hồ sơ nguyên trạng và lưu lại đúng hộp số');

  // Matrix ACL Edit state
  const [editOwnerPosId, setEditOwnerPosId] = useState<string>('POS_CFO_01');
  const [editSignPosId, setEditSignPosId] = useState<string>('POS_CEO_01');
  const [editReviewPosId, setEditReviewPosId] = useState<string>('POS_LEGAL_HEAD');
  const [editReviewPerm, setEditReviewPerm] = useState<'view' | 'comment' | 'edit_section' | 'approve' | 'sign'>('approve');

  // Load data whenever modal opens
  const loadData = async () => {
    if (!document?.id) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const [posRes, govRes, evalRes] = await Promise.all([
        api.getPositions().catch(() => ({ positions: [] })),
        api.getDocumentGovernance(document.id).catch(() => null),
        api.evaluateDocumentPermissions(document.id).catch(() => null)
      ]);

      setPositions(posRes.positions || []);
      if (govRes) {
        setGovernanceData(govRes);
        setLocWarehouse(govRes.physical_archive?.warehouse_name || 'Kho Lưu Trữ Trung Tâm - K02');
        setLocAisle(govRes.physical_archive?.aisle || 'Dãy B');
        setLocShelf(govRes.physical_archive?.shelf || 'Kệ 04');
        setLocBox(govRes.physical_archive?.box_number || 'Hộp 12');
        setLocFolder(govRes.physical_archive?.folder_label || 'Tập hợp đồng tín dụng Q4/2026');
        setLocArchiveCode(govRes.physical_archive?.archive_code || 'ARCHIVE-2026-HN-K02');
        setEditOwnerPosId(govRes.matrix_acl?.owner_position_id || 'POS_CFO_01');
        setEditSignPosId(govRes.matrix_acl?.sign_authority_position_id || 'POS_CEO_01');
      }
      if (evalRes) {
        setPermissionEval(evalRes);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Không thể tải dữ liệu quản trị tài liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && document?.id) {
      loadData();
    }
  }, [isOpen, document?.id]);

  if (!isOpen || !document) return null;

  // Xử lý Zero-Downtime Handover
  const handleExecuteHandover = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOccupantUserId.trim()) {
      setErrorMsg('Vui lòng nhập ID hoặc Email nhân sự nhận bàn giao');
      return;
    }
    setActionLoading(true);
    setErrorMsg(null);
    try {
      const res = await api.handoverPosition(handoverTargetPositionId, {
        new_occupant_user_id: newOccupantUserId.trim(),
        new_occupant_name: newOccupantName.trim() || newOccupantUserId.trim(),
        effective_date: Date.now(),
        handover_note: handoverNote.trim(),
        retain_read_access_for_predecessor: retainReadAccess
      });
      setSuccessMsg(res.message || 'Bàn giao chức vụ thành công không độ trễ!');
      setShowHandoverModal(false);
      setNewOccupantUserId('');
      setNewOccupantName('');
      await loadData();
      onGovernanceUpdated?.();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Lỗi khi bàn giao chức vụ');
    } finally {
      setActionLoading(false);
    }
  };

  // Cập nhật vị trí lưu trữ vật lý
  const handleUpdateLocation = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setErrorMsg(null);
    try {
      await api.updatePhysicalLocation(document.id, {
        warehouse_name: locWarehouse,
        aisle: locAisle,
        shelf: locShelf,
        box_number: locBox,
        folder_label: locFolder,
        archive_code: locArchiveCode,
        has_physical_copy: true,
        note: `Thủ kho điều chỉnh vị trí lưu trữ vào ${locShelf} - ${locBox}`
      });
      setSuccessMsg('Cập nhật vị trí kho vật lý thành công!');
      setShowLocationModal(false);
      await loadData();
      onGovernanceUpdated?.();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Lỗi khi cập nhật vị trí kho');
    } finally {
      setActionLoading(false);
    }
  };

  // Ghi nhận mượn bản cứng
  const handleBorrowSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setErrorMsg(null);
    try {
      const dueDateMs = Date.now() + borrowDueDateDays * 86400000;
      const res = await api.borrowPhysicalDocument(document.id, {
        borrower_position_id: borrowerPosId,
        borrower_user_id: tenant?.id || 'usr_staff',
        borrower_name: borrowerName.trim(),
        due_date: dueDateMs,
        purpose: borrowPurpose.trim()
      });
      setSuccessMsg(res.message || 'Đăng ký mượn bản cứng thành công!');
      setShowBorrowModal(false);
      await loadData();
      onGovernanceUpdated?.();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Lỗi khi đăng ký mượn tài liệu gốc');
    } finally {
      setActionLoading(false);
    }
  };

  // Xác nhận trả bản cứng vào kho
  const handleReturnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setErrorMsg(null);
    try {
      const res = await api.returnPhysicalDocument(document.id, {
        note: returnNote.trim()
      });
      setSuccessMsg(res.message || 'Đã xác nhận trả tài liệu về kệ kho!');
      setShowReturnModal(false);
      await loadData();
      onGovernanceUpdated?.();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Lỗi khi xác nhận hoàn trả');
    } finally {
      setActionLoading(false);
    }
  };

  // Cập nhật ma trận phân quyền Matrix ACL
  const handleSaveMatrixAcl = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setErrorMsg(null);
    try {
      const currentList = governanceData?.matrix_acl?.review_matrix || [];
      const updatedList = [...currentList.filter(r => r.position_id !== editReviewPosId), {
        position_id: editReviewPosId,
        permission: editReviewPerm,
        allowed_sections: editReviewPerm === 'edit_section' ? ['financial_terms', 'annex_a'] : undefined
      }];

      await api.updateDocumentMatrixAcl(document.id, {
        owner_position_id: editOwnerPosId,
        sign_authority_position_id: editSignPosId,
        review_matrix: updatedList,
        inherit_on_occupant_change: true
      });

      setSuccessMsg('Đã cập nhật Ma trận phân quyền Chiếc ghế!');
      setShowMatrixEditModal(false);
      await loadData();
      onGovernanceUpdated?.();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Lỗi khi cập nhật ACL ma trận');
    } finally {
      setActionLoading(false);
    }
  };

  const ownerPos = positions.find(p => p.id === (governanceData?.matrix_acl?.owner_position_id || 'POS_CFO_01')) || governanceData?.owner_position;
  const signPos = positions.find(p => p.id === (governanceData?.matrix_acl?.sign_authority_position_id || 'POS_CEO_01')) || governanceData?.sign_authority_position;
  const physicalRec = governanceData?.physical_archive;

  return (
    <div className="modal-overlay" style={{ background: 'rgba(5, 5, 12, 0.88)', backdropFilter: 'blur(10px)', zIndex: 1100 }}>
      <div
        className="modal-content"
        style={{
          width: '980px',
          maxWidth: '96vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.6)',
          overflow: 'hidden'
        }}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-elevated)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px'
              }}
            >
              🏛️
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                  Quản Trị Chiếc Ghế & Kho Vật Lý
                </h2>
                <span
                  style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: 'rgba(124, 58, 237, 0.2)',
                    color: '#c084fc',
                    border: '1px solid rgba(124, 58, 237, 0.4)',
                    fontWeight: 600
                  }}
                >
                  ENTERPRISE MATRIX
                </span>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '2px 0 0 0' }}>
                Tài liệu: <strong style={{ color: 'var(--text-primary)' }}>{document.filename}</strong> (Mã: {document.id})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: '22px',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '6px'
            }}
          >
            ✕
          </button>
        </div>

        {/* FEEDBACK BANNERS */}
        {errorMsg && (
          <div style={{ padding: '10px 24px', background: 'rgba(239, 68, 68, 0.15)', borderBottom: '1px solid rgba(239, 68, 68, 0.3)', color: '#fca5a5', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>⚠️</span> {errorMsg}
          </div>
        )}
        {successMsg && (
          <div style={{ padding: '10px 24px', background: 'rgba(16, 185, 129, 0.15)', borderBottom: '1px solid rgba(16, 185, 129, 0.3)', color: '#6ee7b7', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>✅</span> {successMsg}
          </div>
        )}

        {/* REALTIME PERMISSION EVALUATION STRIP */}
        {permissionEval && (
          <div
            style={{
              padding: '10px 24px',
              background: permissionEval.can_view ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Thẩm định quyền của bạn:</span>
              <span style={{ fontWeight: 600, color: permissionEval.can_view ? '#10b981' : '#ef4444' }}>
                {permissionEval.can_view ? '👁️ Có quyền xem' : '🚫 Không có quyền xem'}
              </span>
              <span style={{ fontWeight: 600, color: permissionEval.can_edit ? '#10b981' : 'var(--text-muted)' }}>
                {permissionEval.can_edit ? '✏️ Có quyền sửa' : '🔒 Khóa sửa'}
              </span>
              <span style={{ fontWeight: 600, color: permissionEval.can_sign ? '#06b6d4' : 'var(--text-muted)' }}>
                {permissionEval.can_sign ? '✍️ Thẩm quyền ký duyệt' : '⛔ Không có quyền ký'}
              </span>
              {permissionEval.is_owner && (
                <span style={{ background: '#7c3aed', color: '#fff', padding: '1px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                  CHỦ SỞ HỮU CHIẾC GHẾ
                </span>
              )}
            </div>
            {permissionEval.acting_as_position && (
              <div style={{ color: 'var(--accent-cyan)', fontWeight: 500 }}>
                Nhân danh: <strong>{permissionEval.acting_as_position.title}</strong>
              </div>
            )}
          </div>
        )}

        {/* TABS NAVIGATION */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--bg-base)' }}>
          <button
            onClick={() => setActiveTab('matrix')}
            style={{
              flex: 1,
              padding: '14px',
              background: activeTab === 'matrix' ? 'var(--bg-surface)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'matrix' ? '2px solid var(--accent-primary)' : '2px solid transparent',
              color: activeTab === 'matrix' ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>🪑</span> Quyền Sở Hữu Theo Vị Trí (Position Matrix)
          </button>
          <button
            onClick={() => setActiveTab('physical')}
            style={{
              flex: 1,
              padding: '14px',
              background: activeTab === 'physical' ? 'var(--bg-surface)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'physical' ? '2px solid var(--accent-cyan)' : '2px solid transparent',
              color: activeTab === 'physical' ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>📦</span> Kho Lưu Trữ Vật Lý (Dual Physical Archive)
          </button>
        </div>

        {/* TAB CONTENTS */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
              Đang tải dữ liệu ma trận quản trị...
            </div>
          ) : activeTab === 'matrix' ? (
            /* ─────────────────────────────────────────────────────────────────
               TAB 1: POSITION MATRIX OWNERSHIP
               ───────────────────────────────────────────────────────────────── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* PRIMARY SEAT BADGE CARD */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.12) 0%, rgba(6, 182, 212, 0.08) 100%)',
                  border: '1px solid rgba(124, 58, 237, 0.3)',
                  borderRadius: 'var(--radius-md)',
                  padding: '20px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px'
                }}
              >
                {/* OWNER SEAT */}
                <div>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c084fc', fontWeight: 600, marginBottom: '6px' }}>
                    👑 Chiếc Ghế Sở Hữu Chính (Owner Position)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '28px' }}>🪑</div>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                        {ownerPos?.title || 'Giám Đốc Tài Chính (CFO)'}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Mã vị trí: <code style={{ color: 'var(--accent-cyan)' }}>{ownerPos?.id || 'POS_CFO_01'}</code> • {ownerPos?.department_name || 'Khối Tài Chính'}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '14px', padding: '10px 12px', background: 'rgba(0, 0, 0, 0.25)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Nhân sự đang đương nhiệm ghế:</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>
                      {ownerPos?.current_occupant_name || ownerPos?.current_occupant_user_id || 'Chưa phân bổ'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      User ID: <code>{ownerPos?.current_occupant_user_id || 'N/A'}</code>
                    </div>
                  </div>
                </div>

                {/* SIGN AUTHORITY SEAT */}
                <div>
                  <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#38bdf8', fontWeight: 600, marginBottom: '6px' }}>
                    ✍️ Chiếc Ghế Ký Thẩm Quyền (Sign Authority)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '28px' }}>🖋️</div>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                        {signPos?.title || 'Tổng Giám Đốc (CEO)'}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Mã vị trí: <code style={{ color: 'var(--accent-cyan)' }}>{signPos?.id || 'POS_CEO_01'}</code> • {signPos?.department_name || 'Ban Điều Hành'}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '14px', padding: '10px 12px', background: 'rgba(0, 0, 0, 0.25)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Người đại diện ký duyệt hiện tại:</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>
                      {signPos?.current_occupant_name || signPos?.current_occupant_user_id || 'Chưa phân bổ'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      User ID: <code>{signPos?.current_occupant_user_id || 'N/A'}</code>
                    </div>
                  </div>
                </div>
              </div>

              {/* ACTION TOOLBAR: ZERO-DOWNTIME HANDOVER & ACL CONFIG */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      setHandoverTargetPositionId(ownerPos?.id || 'POS_CFO_01');
                      setShowHandoverModal(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>⚡</span> Bàn Giao Chức Vụ (Zero-Downtime Handover)
                  </button>

                  <button
                    onClick={() => setShowMatrixEditModal(true)}
                    style={{
                      padding: '8px 14px',
                      background: 'var(--bg-glass)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text-primary)',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    ⚙️ Cấu Hình Ma Trận Phê Duyệt
                  </button>
                </div>

                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  Kế thừa tự động: <strong style={{ color: '#10b981' }}>BẬT (Zero-downtime)</strong>
                </div>
              </div>

              {/* REVIEW MATRIX TABLE */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)' }}>
                  📋 Ma Trận Soát Xét & Phê Duyệt Liên Phòng Ban (Review Matrix)
                </h4>
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                    <thead style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border)' }}>
                      <tr>
                        <th style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>Vị trí / Chiếc ghế</th>
                        <th style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>Người đương nhiệm</th>
                        <th style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>Quyền hạn</th>
                        <th style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>Phạm vi cho phép</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(governanceData?.matrix_acl?.review_matrix || []).map((entry, idx) => {
                        const posInfo = positions.find(p => p.id === entry.position_id);
                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                              {posInfo?.title || entry.position_id}
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{entry.position_id}</div>
                            </td>
                            <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>
                              {posInfo?.current_occupant_name || posInfo?.current_occupant_user_id || 'Chưa bổ nhiệm'}
                            </td>
                            <td style={{ padding: '10px 14px' }}>
                              <span
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background:
                                    entry.permission === 'sign' ? 'rgba(6, 182, 212, 0.2)' :
                                    entry.permission === 'approve' ? 'rgba(16, 185, 129, 0.2)' :
                                    entry.permission === 'edit_section' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                  color:
                                    entry.permission === 'sign' ? '#06b6d4' :
                                    entry.permission === 'approve' ? '#10b981' :
                                    entry.permission === 'edit_section' ? '#f59e0b' : 'var(--text-primary)'
                                }}
                              >
                                {entry.permission.toUpperCase()}
                              </span>
                            </td>
                            <td style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--text-muted)' }}>
                              {entry.allowed_sections && entry.allowed_sections.length > 0
                                ? `Trường: ${entry.allowed_sections.join(', ')}`
                                : 'Toàn bộ tài liệu'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* HANDOVER AUDIT TRAIL */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)' }}>
                  📜 Lịch Sử Bàn Giao Chức Vụ Bất Biến (Handover Audit Trail)
                </h4>
                {(governanceData?.handovers || []).length === 0 ? (
                  <div style={{ padding: '14px', background: 'var(--bg-glass)', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                    Chưa có đợt bàn giao nào được ghi nhận cho chiếc ghế này.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(governanceData?.handovers || []).map((h, i) => (
                      <div
                        key={i}
                        style={{
                          padding: '12px 14px',
                          background: 'var(--bg-glass)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontSize: '12px'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {h.previous_occupant_name || h.previous_occupant_user_id || 'Khởi tạo ban đầu'} ➔ <span style={{ color: 'var(--accent-cyan)' }}>{h.new_occupant_name || h.new_occupant_user_id}</span>
                          </div>
                          <div style={{ color: 'var(--text-muted)', marginTop: '2px' }}>
                            {h.handover_note}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: 'var(--text-secondary)' }}>
                            {new Date(h.effective_date).toLocaleDateString('vi-VN')}
                          </div>
                          <div style={{ fontSize: '11px', color: h.retain_read_access_for_predecessor ? '#10b981' : '#f59e0b' }}>
                            {h.retain_read_access_for_predecessor ? 'Đã giữ quyền đọc cho người cũ' : 'Thu hồi quyền người cũ'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ─────────────────────────────────────────────────────────────────
               TAB 2: DUAL PHYSICAL ARCHIVE
               ───────────────────────────────────────────────────────────────── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* PHYSICAL LOCATION VISUAL HIERARCHY */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                    🗺️ Sơ Đồ Định Vị Hồ Sơ Vật Lý Trong Kho Thực Tế
                  </h4>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 700,
                      background:
                        physicalRec?.physical_status === 'borrowed' ? 'rgba(245, 158, 11, 0.2)' :
                        physicalRec?.physical_status === 'transferred' ? 'rgba(6, 182, 212, 0.2)' :
                        physicalRec?.physical_status === 'disposed' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                      color:
                        physicalRec?.physical_status === 'borrowed' ? '#f59e0b' :
                        physicalRec?.physical_status === 'transferred' ? '#06b6d4' :
                        physicalRec?.physical_status === 'disposed' ? '#ef4444' : '#10b981',
                      border: '1px solid currentColor'
                    }}
                  >
                    {physicalRec?.physical_status === 'borrowed' ? '⚠️ ĐANG CHO MƯỢN' :
                     physicalRec?.physical_status === 'transferred' ? '🔄 ĐÃ LUÂN CHUYỂN' :
                     physicalRec?.physical_status === 'disposed' ? '🗑️ ĐÃ TIÊU HỦY' : '✅ TRONG KHO (IN VAULT)'}
                  </span>
                </div>

                {/* HIERARCHY STEP CARDS */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: '10px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ padding: '14px 10px', background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>1. Kho lưu trữ</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', marginTop: '4px' }}>
                      {physicalRec?.warehouse_name || 'Kho K02'}
                    </div>
                  </div>
                  <div style={{ padding: '14px 10px', background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>2. Dãy</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--accent-cyan)', marginTop: '4px' }}>
                      {physicalRec?.aisle || 'Dãy B'}
                    </div>
                  </div>
                  <div style={{ padding: '14px 10px', background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>3. Kệ</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#c084fc', marginTop: '4px' }}>
                      {physicalRec?.shelf || 'Kệ 04'}
                    </div>
                  </div>
                  <div style={{ padding: '14px 10px', background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>4. Hộp số</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#f59e0b', marginTop: '4px' }}>
                      {physicalRec?.box_number || 'Hộp 12'}
                    </div>
                  </div>
                  <div style={{ padding: '14px 10px', background: 'var(--bg-glass)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>5. Mã hồ sơ</div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#10b981', marginTop: '4px', wordBreak: 'break-all' }}>
                      {physicalRec?.archive_code || 'ARCHIVE-2026-HN-K02'}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  📁 Nhãn kẹp bìa còng: <strong style={{ color: 'var(--text-primary)' }}>{physicalRec?.folder_label || 'Hồ sơ bản cứng gốc'}</strong>
                </div>
              </div>

              {/* CURRENT BORROWER CARD (IF BORROWED) */}
              {physicalRec?.physical_status === 'borrowed' && physicalRec.current_borrower && (
                <div
                  style={{
                    padding: '16px',
                    background: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    borderRadius: 'var(--radius-md)'
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>⚠️</span> Thông Tin Người Đang Mượn Bản Cứng Gốc:
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '10px', fontSize: '12px' }}>
                    <div>
                      <div style={{ color: 'var(--text-muted)' }}>Họ tên người mượn:</div>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{physicalRec.current_borrower.borrower_name}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-muted)' }}>Vị trí / Chức danh:</div>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{physicalRec.current_borrower.borrower_position_id}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-muted)' }}>Hạn hoàn trả:</div>
                      <div style={{ fontWeight: 600, color: '#f87171' }}>
                        {new Date(physicalRec.current_borrower.due_date).toLocaleDateString('vi-VN')}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Lý do mượn: <em>{physicalRec.current_borrower.purpose}</em>
                  </div>
                </div>
              )}

              {/* ACTION BUTTONS & QR BARCODE */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '20px' }}>
                {/* ACTION BUTTONS */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                    Thao Tác Thủ Kho & Quản Trị Bản Cứng
                  </h4>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {physicalRec?.physical_status !== 'borrowed' ? (
                      <button
                        onClick={() => setShowBorrowModal(true)}
                        style={{
                          padding: '10px 18px',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>📋</span> Đăng Ký Mượn Bản Cứng
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowReturnModal(true)}
                        style={{
                          padding: '10px 18px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <span>📥</span> Xác Nhận Trả Kho
                      </button>
                    )}

                    <button
                      onClick={() => setShowLocationModal(true)}
                      style={{
                        padding: '10px 14px',
                        background: 'var(--bg-glass)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--text-primary)',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Cập Nhật Vị Trí Kệ / Hộp
                    </button>
                  </div>

                  <div style={{ padding: '12px', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    💡 <strong>Nguyên lý Kho Song Hành</strong>: Mọi thay đổi vị trí hộp hoặc lượt mượn trả bản cứng đều được đồng bộ tức thì vào metadata tài liệu số Erix One để phục vụ kiểm toán tài chính và bảo đảm hồ sơ không bị thất lạc.
                  </div>
                </div>

                {/* QR CODE & BARCODE DISPLAY */}
                <div
                  style={{
                    padding: '16px',
                    background: '#fff',
                    borderRadius: 'var(--radius-md)',
                    color: '#000',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.2)'
                  }}
                >
                  <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', marginBottom: '8px' }}>
                    NHÃN LƯU TRỮ VẬT LÝ • ERIX VAULT
                  </div>

                  {/* QR SVG MOCK/RENDER */}
                  <div
                    style={{
                      width: '120px',
                      height: '120px',
                      background: '#000',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px'
                    }}
                  >
                    <svg viewBox="0 0 100 100" width="100%" height="100%">
                      <rect width="100" height="100" fill="#fff" />
                      {/* Standard QR Pattern representation */}
                      <rect x="10" y="10" width="25" height="25" fill="#000" />
                      <rect x="15" y="15" width="15" height="15" fill="#fff" />
                      <rect x="18" y="18" width="9" height="9" fill="#000" />

                      <rect x="65" y="10" width="25" height="25" fill="#000" />
                      <rect x="70" y="15" width="15" height="15" fill="#fff" />
                      <rect x="73" y="18" width="9" height="9" fill="#000" />

                      <rect x="10" y="65" width="25" height="25" fill="#000" />
                      <rect x="15" y="70" width="15" height="15" fill="#fff" />
                      <rect x="18" y="73" width="9" height="9" fill="#000" />

                      <rect x="42" y="42" width="16" height="16" fill="#000" />
                      <rect x="42" y="15" width="8" height="8" fill="#000" />
                      <rect x="15" y="42" width="8" height="8" fill="#000" />
                      <rect x="75" y="45" width="12" height="12" fill="#000" />
                      <rect x="45" y="75" width="12" height="12" fill="#000" />
                      <rect x="65" y="65" width="10" height="10" fill="#000" />
                    </svg>
                  </div>

                  <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '13px', marginTop: '8px', color: '#0f172a' }}>
                    {physicalRec?.archive_code || 'ARCHIVE-2026-HN-K02'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>
                    Vị trí: {physicalRec?.shelf} • {physicalRec?.box_number}
                  </div>

                  <button
                    onClick={() => window.print()}
                    style={{
                      marginTop: '8px',
                      padding: '4px 12px',
                      background: '#0f172a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    🖨️ In Nhãn Dán Bìa Hồ Sơ
                  </button>
                </div>
              </div>

              {/* PHYSICAL ARCHIVE HISTORY */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)' }}>
                  ⏱️ Nhật Ký Mượn / Trả & Luân Chuyển Bản Cứng (Physical Archive History)
                </h4>
                {(physicalRec?.history || []).length === 0 ? (
                  <div style={{ padding: '14px', background: 'var(--bg-glass)', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                    Chưa có lịch sử mượn trả nào cho tài liệu gốc này.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(physicalRec?.history || []).map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '10px 14px',
                          background: 'var(--bg-glass)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontSize: '12px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>
                            {item.action === 'archived' ? '📥' :
                             item.action === 'borrowed' ? '📤' :
                             item.action === 'returned' ? '↩️' : '🚚'}
                          </span>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                              {item.action.toUpperCase()}: {item.note}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              Thực hiện bởi: {item.performed_by_name || item.performed_by_user_id} ({item.performed_by_position_id})
                            </div>
                          </div>
                        </div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                          {new Date(item.timestamp).toLocaleString('vi-VN')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ─── SUB-MODAL: ZERO-DOWNTIME POSITION HANDOVER FORM ─── */}
        {showHandoverModal && (
          <div className="modal-overlay" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.75)' }}>
            <div className="modal-content" style={{ width: '540px', padding: '24px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
                  ⚡ Bàn Giao Chức Vụ (Zero-Downtime Handover)
                </h3>
                <button onClick={() => setShowHandoverModal(false)} style={{ background: 'transparent', border: 'none', color: '#999', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>

              <form onSubmit={handleExecuteHandover} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Chiếc ghế cần bàn giao:
                  </label>
                  <select
                    value={handoverTargetPositionId}
                    onChange={e => setHandoverTargetPositionId(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  >
                    {positions.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.title} ({p.id}) - Đương nhiệm: {p.current_occupant_name || p.current_occupant_user_id}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    User ID / Email nhân sự mới nhận ghế:
                  </label>
                  <input
                    type="text"
                    placeholder="vd: usr_lethuha_02 hoặc legal@erix.vn"
                    value={newOccupantUserId}
                    onChange={e => setNewOccupantUserId(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Họ và tên nhân sự mới:
                  </label>
                  <input
                    type="text"
                    placeholder="vd: Lê Thu Hà"
                    value={newOccupantName}
                    onChange={e => setNewOccupantName(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Căn cứ bổ nhiệm / Quyết định:
                  </label>
                  <input
                    type="text"
                    value={handoverNote}
                    onChange={e => setHandoverNote(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                  <input
                    type="checkbox"
                    id="retain_read"
                    checked={retainReadAccess}
                    onChange={e => setRetainReadAccess(e.target.checked)}
                  />
                  <label htmlFor="retain_read" style={{ fontSize: '12px', color: 'var(--text-primary)', cursor: 'pointer' }}>
                    Giữ quyền chỉ đọc (Read-only) cho nhân sự tiền nhiệm
                  </label>
                </div>

                <div style={{ padding: '10px', background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.3)', borderRadius: '6px', fontSize: '11px', color: '#c084fc' }}>
                  ⚡ Quyền của hàng ngàn tài liệu thuộc chiếc ghế này sẽ tự động chuyển sang nhân sự mới trong 1 giây mà không cần thay đổi từng file.
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowHandoverModal(false)}
                    style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    style={{ padding: '8px 20px', background: 'var(--accent-primary)', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  >
                    {actionLoading ? 'Đang chuyển giao...' : 'Xác Nhận Bàn Giao Tức Thì'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── SUB-MODAL: BORROW PHYSICAL DOCUMENT ─── */}
        {showBorrowModal && (
          <div className="modal-overlay" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.75)' }}>
            <div className="modal-content" style={{ width: '500px', padding: '24px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
                  📋 Đăng Ký Mượn Bản Cứng Gốc Tại Kho
                </h3>
                <button onClick={() => setShowBorrowModal(false)} style={{ background: 'transparent', border: 'none', color: '#999', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>

              <form onSubmit={handleBorrowSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Vị trí / Chức danh người mượn:
                  </label>
                  <select
                    value={borrowerPosId}
                    onChange={e => setBorrowerPosId(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  >
                    {positions.map(p => (
                      <option key={p.id} value={p.id}>{p.title} ({p.id})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Họ và tên người mượn:
                  </label>
                  <input
                    type="text"
                    value={borrowerName}
                    onChange={e => setBorrowerName(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Thời hạn mượn (Số ngày):
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={borrowDueDateDays}
                    onChange={e => setBorrowDueDateDays(parseInt(e.target.value) || 7)}
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Mục đích mượn tài liệu gốc:
                  </label>
                  <textarea
                    rows={3}
                    value={borrowPurpose}
                    onChange={e => setBorrowPurpose(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowBorrowModal(false)}
                    style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    style={{ padding: '8px 20px', background: 'var(--accent-amber)', border: 'none', borderRadius: '6px', color: '#000', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {actionLoading ? 'Đang ghi nhận...' : 'Xác Nhận Xuất Kho Mượn'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── SUB-MODAL: RETURN PHYSICAL DOCUMENT ─── */}
        {showReturnModal && (
          <div className="modal-overlay" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.75)' }}>
            <div className="modal-content" style={{ width: '480px', padding: '24px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
                  📥 Xác Nhận Hoàn Trả Tài Liệu Vào Kho
                </h3>
                <button onClick={() => setShowReturnModal(false)} style={{ background: 'transparent', border: 'none', color: '#999', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>

              <form onSubmit={handleReturnSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Người đang mượn: <strong style={{ color: '#fff' }}>{physicalRec?.current_borrower?.borrower_name}</strong>
                  <br />
                  Vị trí hoàn trả: <strong>{physicalRec?.shelf} • {physicalRec?.box_number}</strong>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Ghi chú kiểm tra tình trạng vật lý (con dấu, số trang):
                  </label>
                  <textarea
                    rows={3}
                    value={returnNote}
                    onChange={e => setReturnNote(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}
                  />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowReturnModal(false)}
                    style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    style={{ padding: '8px 20px', background: 'var(--accent-green)', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                  >
                    {actionLoading ? 'Đang lưu...' : 'Nhập Lại Vào Kho'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── SUB-MODAL: UPDATE PHYSICAL LOCATION ─── */}
        {showLocationModal && (
          <div className="modal-overlay" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.75)' }}>
            <div className="modal-content" style={{ width: '520px', padding: '24px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
                  ✏️ Điều Chỉnh Vị Trí Kho Vật Lý
                </h3>
                <button onClick={() => setShowLocationModal(false)} style={{ background: 'transparent', border: 'none', color: '#999', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>

              <form onSubmit={handleUpdateLocation} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Tên kho:</label>
                  <input type="text" value={locWarehouse} onChange={e => setLocWarehouse(e.target.value)} required style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Dãy:</label>
                  <input type="text" value={locAisle} onChange={e => setLocAisle(e.target.value)} required style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Kệ:</label>
                  <input type="text" value={locShelf} onChange={e => setLocShelf(e.target.value)} required style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Hộp số:</label>
                  <input type="text" value={locBox} onChange={e => setLocBox(e.target.value)} required style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Mã lưu trữ:</label>
                  <input type="text" value={locArchiveCode} onChange={e => setLocArchiveCode(e.target.value)} required style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }} />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Nhãn kẹp hồ sơ:</label>
                  <input type="text" value={locFolder} onChange={e => setLocFolder(e.target.value)} required style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }} />
                </div>

                <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                  <button type="button" onClick={() => setShowLocationModal(false)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-secondary)', cursor: 'pointer' }}>Hủy</button>
                  <button type="submit" disabled={actionLoading} style={{ padding: '8px 20px', background: 'var(--accent-cyan)', border: 'none', borderRadius: '6px', color: '#000', fontWeight: 700, cursor: 'pointer' }}>
                    {actionLoading ? 'Đang lưu...' : 'Lưu Vị Trí Kho'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── SUB-MODAL: EDIT MATRIX ACL ─── */}
        {showMatrixEditModal && (
          <div className="modal-overlay" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.75)' }}>
            <div className="modal-content" style={{ width: '520px', padding: '24px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
                  ⚙️ Cấu Hình Ma Trận Phân Quyền Chiếc Ghế
                </h3>
                <button onClick={() => setShowMatrixEditModal(false)} style={{ background: 'transparent', border: 'none', color: '#999', fontSize: '18px', cursor: 'pointer' }}>✕</button>
              </div>

              <form onSubmit={handleSaveMatrixAcl} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Chiếc ghế sở hữu (Owner):</label>
                  <select value={editOwnerPosId} onChange={e => setEditOwnerPosId(e.target.value)} style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}>
                    {positions.map(p => (
                      <option key={p.id} value={p.id}>{p.title} ({p.id})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Chiếc ghế ký duyệt (Sign Authority):</label>
                  <select value={editSignPosId} onChange={e => setEditSignPosId(e.target.value)} style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}>
                    {positions.map(p => (
                      <option key={p.id} value={p.id}>{p.title} ({p.id})</option>
                    ))}
                  </select>
                </div>

                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--accent-cyan)', marginBottom: '8px' }}>Thêm vị trí liên đới soát xét:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '10px' }}>
                    <select value={editReviewPosId} onChange={e => setEditReviewPosId(e.target.value)} style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}>
                      {positions.map(p => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>

                    <select value={editReviewPerm} onChange={e => setEditReviewPerm(e.target.value as any)} style={{ width: '100%', padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: '#fff' }}>
                      <option value="view">Chỉ xem (View)</option>
                      <option value="comment">Bình luận (Comment)</option>
                      <option value="edit_section">Sửa đoạn (Edit Section)</option>
                      <option value="approve">Phê duyệt (Approve)</option>
                      <option value="sign">Ký duyệt (Sign)</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                  <button type="button" onClick={() => setShowMatrixEditModal(false)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-secondary)', cursor: 'pointer' }}>Hủy</button>
                  <button type="submit" disabled={actionLoading} style={{ padding: '8px 20px', background: 'var(--accent-primary)', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                    {actionLoading ? 'Đang lưu...' : 'Lưu Cấu Hình Ma Trận'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL FOOTER */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--bg-elevated)'
          }}
        >
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Erix One Enterprise Vault Engine • Chuẩn lưu trữ tổ chức số & kho vật lý
          </div>
          <button
            onClick={onClose}
            className="btn btn--secondary"
            style={{ padding: '6px 18px', fontSize: '13px', cursor: 'pointer' }}
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}
